import React, { useState, useEffect, useCallback } from 'react';
import { playbooksAPI } from '../services/api';
import PlaybookFormModal from '../components/PlaybookFormModal';
import './Playbooks.css';

const EMPTY_FORM = {
  name:                '',
  alert_name_pattern:  '',
  server_type:         '',
  severity:            '',
  namespace_pattern:   '',
  instructions:        '',
  log_paths_raw:       '',   // comma/newline separated in UI, converted to array on save
  diagnostic_hints_raw:'',   // one command per line
  resolution_steps_raw:'',   // one step per line: "Title | command | destructive"
  is_active:           true,
};

const SERVER_TYPE_OPTIONS = [
  { value: '',          label: 'Any (K8s + Baremetal)' },
  { value: 'k8s',       label: 'Kubernetes only' },
  { value: 'baremetal', label: 'Baremetal only' },
];

const SEVERITY_OPTIONS = [
  { value: '',         label: 'Any severity' },
  { value: 'critical', label: 'Critical' },
  { value: 'warning',  label: 'Warning' },
  { value: 'info',     label: 'Info' },
];

export default function Playbooks() {
  const [playbooks, setPlaybooks] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [editId, setEditId]         = useState(null);   // null = create
  const [form, setForm]             = useState(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState(null);

  // Preview state
  const [previewAlert, setPreviewAlert] = useState('');
  const [previewResult, setPreviewResult] = useState(null);
  const [previewing, setPreviewing]       = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await playbooksAPI.list();
      setPlaybooks(res.data);
    } catch (e) {
      setError('Failed to load playbooks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const openEdit = (pb) => {
    setEditId(pb.id);
    setForm({
      name:               pb.name,
      alert_name_pattern: pb.alert_name_pattern || '',
      server_type:        pb.server_type || '',
      severity:           pb.severity || '',
      namespace_pattern:  pb.namespace_pattern || '',
      instructions:       pb.instructions || '',
      log_paths_raw:       (pb.log_paths || []).join('\n'),
      diagnostic_hints_raw:(pb.diagnostic_hints || []).join('\n'),
      resolution_steps_raw:(pb.resolution_steps || [])
        .map(s => [s.title || '', s.command || '', s.is_destructive ? 'destructive' : '']
          .filter(Boolean).join(' | ')).join('\n'),
      is_active:           pb.is_active,
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditId(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setFormError('Name is required'); return; }
    setSaving(true);
    setFormError(null);
    try {
      const logPaths = form.log_paths_raw
        .split(/[\n,]+/)
        .map(s => s.trim())
        .filter(Boolean);
      const diagHints = form.diagnostic_hints_raw
        .split(/\n+/)
        .map(s => s.trim())
        .filter(Boolean);
      // Each line: "Title | command | destructive" (command + destructive optional)
      const resSteps = form.resolution_steps_raw
        .split(/\n+/)
        .map(s => s.trim())
        .filter(Boolean)
        .map(line => {
          const [title, command, flag] = line.split('|').map(x => (x || '').trim());
          const destructive = /destruct/i.test(flag || '');
          return {
            title: title || 'step',
            action_type: command ? 'kubectl_exec' : 'manual',
            command: command || '',
            is_destructive: destructive,
            requires_approval: destructive,
            rollback: null,
            notes: '',
          };
        });

      const payload = {
        name:               form.name.trim(),
        alert_name_pattern: form.alert_name_pattern.trim() || null,
        server_type:        form.server_type || null,
        severity:           form.severity || null,
        namespace_pattern:  form.namespace_pattern.trim() || null,
        instructions:       form.instructions.trim() || null,
        log_paths:          logPaths.length ? logPaths : null,
        diagnostic_hints:   diagHints.length ? diagHints : null,
        resolution_steps:   resSteps.length ? resSteps : null,
        is_active:          form.is_active,
      };

      if (editId) {
        await playbooksAPI.update(editId, payload);
      } else {
        await playbooksAPI.create(payload);
      }
      await load();
      closeModal();
    } catch (e) {
      setFormError(e.response?.data?.detail || 'Failed to save playbook');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this playbook?')) return;
    try {
      await playbooksAPI.delete(id);
      setPlaybooks(pb => pb.filter(p => p.id !== id));
    } catch (e) {
      alert('Failed to delete playbook');
    }
  };

  const handleToggle = async (pb) => {
    try {
      const res = await playbooksAPI.update(pb.id, { is_active: !pb.is_active });
      setPlaybooks(prev => prev.map(p => p.id === pb.id ? res.data : p));
      await load();
    } catch (e) {
      alert('Failed to update playbook');
    }
  };

  const handlePreview = async () => {
    if (!previewAlert.trim()) return;
    setPreviewing(true);
    setPreviewResult(null);
    try {
      const res = await playbooksAPI.preview({ alert_name: previewAlert.trim() });
      setPreviewResult(res.data);
    } catch (e) {
      setPreviewResult({ error: 'Preview failed' });
    } finally {
      setPreviewing(false);
    }
  };

  const matchBadge = (pb) => {
    const parts = [];
    if (pb.alert_name_pattern && pb.alert_name_pattern !== '*') parts.push(`alert: "${pb.alert_name_pattern}"`);
    if (pb.server_type)   parts.push(pb.server_type);
    if (pb.severity)      parts.push(pb.severity);
    if (pb.namespace_pattern) parts.push(`ns: "${pb.namespace_pattern}"`);
    return parts.length ? parts : ['matches all alerts'];
  };

  return (
    <div className="playbooks-page">
      <div className="playbooks-header">
        <div>
          <h1>Alert Playbooks</h1>
          <p className="playbooks-subtitle">
            SRE-defined instructions injected into Claude's prompt for matching alerts.
            Baremetal rules, log paths, and team-specific notes — all applied automatically.
          </p>
        </div>
        <button className="btn-primary" onClick={openCreate}>+ New Playbook</button>
      </div>

      {/* Preview Tool */}
      <div className="playbooks-preview-box">
        <h3>Test: which playbooks fire for an alert?</h3>
        <div className="preview-row">
          <input
            className="preview-input"
            placeholder='Alert name, e.g. "DiskPressure" or "Host Physical Component Too Hot"'
            value={previewAlert}
            onChange={e => setPreviewAlert(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handlePreview()}
          />
          <button className="btn-preview" onClick={handlePreview} disabled={previewing}>
            {previewing ? 'Checking...' : 'Preview'}
          </button>
        </div>
        {previewResult && (
          <div className="preview-result">
            {previewResult.error
              ? <span className="preview-error">{previewResult.error}</span>
              : previewResult.matched_count === 0
                ? <span className="preview-none">No playbooks would fire for this alert</span>
                : <>
                    <span className="preview-count">{previewResult.matched_count} playbook(s) would fire:</span>
                    <ul className="preview-list">
                      {previewResult.playbooks.map(p => (
                        <li key={p.id}><strong>{p.name}</strong> — {matchBadge(p).join(', ')}</li>
                      ))}
                    </ul>
                    {previewResult.prompt_section && (
                      <details className="preview-prompt">
                        <summary>See prompt section Claude would receive</summary>
                        <pre>{previewResult.prompt_section}</pre>
                      </details>
                    )}
                  </>
            }
          </div>
        )}
      </div>

      {/* Playbook List */}
      {loading && <p className="pb-loading">Loading...</p>}
      {error   && <p className="pb-error">{error}</p>}

      {!loading && playbooks.length === 0 && (
        <div className="pb-empty">
          <p>No playbooks yet.</p>
          <p>Create your first one — e.g. a "Disk Space" playbook that tells Claude to check <code>/var/log/</code> first, or a "Baremetal" playbook that always runs SSH-based checks.</p>
          <button className="btn-primary" onClick={openCreate}>Create First Playbook</button>
        </div>
      )}

      <div className="pb-list">
        {playbooks.map(pb => (
          <div key={pb.id} className={`pb-card ${pb.is_active ? '' : 'pb-card-inactive'}`}>
            <div className="pb-card-header">
              <div className="pb-card-title">
                <span className="pb-name">{pb.name}</span>
                <span className={`pb-status ${pb.is_active ? 'pb-active' : 'pb-inactive'}`}>
                  {pb.is_active ? 'Active' : 'Disabled'}
                </span>
              </div>
              <div className="pb-card-actions">
                <button className="btn-toggle" onClick={() => handleToggle(pb)}>
                  {pb.is_active ? 'Disable' : 'Enable'}
                </button>
                <button className="btn-edit" onClick={() => openEdit(pb)}>Edit</button>
                <button className="btn-delete" onClick={() => handleDelete(pb.id)}>Delete</button>
              </div>
            </div>

            {/* Match criteria tags */}
            <div className="pb-tags">
              {matchBadge(pb).map((tag, i) => (
                <span key={i} className="pb-tag">{tag}</span>
              ))}
            </div>

            {pb.instructions && (
              <div className="pb-instructions">
                <span className="pb-section-label">Instructions for Claude:</span>
                <pre className="pb-instructions-text">{pb.instructions}</pre>
              </div>
            )}

            {pb.log_paths?.length > 0 && (
              <div className="pb-logpaths">
                <span className="pb-section-label">Log paths to check:</span>
                <div className="pb-paths">
                  {pb.log_paths.map((p, i) => <code key={i} className="pb-path">{p}</code>)}
                </div>
              </div>
            )}

            {pb.diagnostic_hints?.length > 0 && (
              <div className="pb-logpaths">
                <span className="pb-section-label">Diagnostic commands ({pb.diagnostic_hints.length}):</span>
                <div className="pb-paths">
                  {pb.diagnostic_hints.map((cmd, i) => <code key={i} className="pb-path pb-cmd">{cmd}</code>)}
                </div>
              </div>
            )}

            {pb.resolution_steps?.length > 0 && (
              <div className="pb-logpaths">
                <span className="pb-section-label">Resolution steps ({pb.resolution_steps.length}):</span>
                <ol className="pb-paths" style={{ margin: '4px 0 0 18px', padding: 0 }}>
                  {pb.resolution_steps.map((st, i) => (
                    <li key={i} style={{ marginBottom: 4, fontSize: 12 }}>
                      {st.title}
                      {st.is_destructive && <span style={{ color: 'var(--color-warning-500)', marginLeft: 6 }}>⚠ needs approval</span>}
                      {st.command && <div><code className="pb-path pb-cmd">{st.command}</code></div>}
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Create / Edit Modal */}
      {showModal && (
        <PlaybookFormModal
          editId={editId}
          initialForm={form}
          onSaved={() => load()}
          onClose={closeModal}
        />
      )}
    </div>
  );
}
